import React, { useEffect, useMemo, useState } from "react";
import type { SourceDto, UpdateSourceDto } from "../models/Sources";
import { useApiClient } from '../components/useApiClient';
import useAuthToken from '../functions/useAuthToken';

const norm = (s: string) => s.trim().toLowerCase();

export default function AdminSourcesPage() {
    const overlayStyle: React.CSSProperties = {
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0,0,0,0.4)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000
    };

    const modalStyle: React.CSSProperties = {
        background: "white",
        padding: 24,
        borderRadius: 8,
        width: 400,
        maxWidth: "90%"
    };


    const [items, setItems] = useState<SourceDto[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    //API
    const getAccessToken = useAuthToken();
    const { get, put, putJson, post, postJson, delete: del } = useApiClient();

    // Add
    const [newType, setNewType] = useState("");

    // Edit
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editingType, setEditingType] = useState("");

    //Delete
    const [deleteTarget, setDeleteTarget] = useState<SourceDto | null>(null);
    const [deleting, setDeleting] = useState(false);


    const existingNorms = useMemo(() => new Set(items.map(i => norm(i.type))), [items]);

    async function load() {
        setError(null);
        setLoading(true);
        try {
            const accessToken = await getAccessToken();
            if (!accessToken) throw new Error('Invalid access token');

            const data = await get<SourceDto[]>("admin/sources",accessToken);

            setItems(data);
        } catch (e: any) {
            setError(e?.response?.data?.detail ?? e?.message ?? "Failed to load sources.");
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        void load();
    }, []);

    function startEdit(item: SourceDto) {
        setEditingId(item.id);
        setEditingType(item.type);
        setError(null);
    }

    function cancelEdit() {
        setEditingId(null);
        setEditingType("");
        setError(null);
    }

    async function addSource() {
        const type = newType.trim();
        if (!type) return;

        // client-side duplicate check (still handle 409 from API)
        if (existingNorms.has(norm(type))) {
            setError(`"${type}" already exists.`);
            return;
        }

        setError(null);
        try {
            const accessToken = await getAccessToken();
            if (!accessToken) throw new Error('Invalid access token');

            const dto: UpdateSourceDto = {
                type: type,
            }
            const created = await postJson<SourceDto>("admin/sources", accessToken, JSON.stringify(dto));

            setItems(prev => [...prev, created].sort((a, b) => a.type.localeCompare(b.type)));
            setNewType("");
        } catch (e: any) {
            const status = e?.response?.status;
            if (status === 409) setError(e?.response?.data?.detail ?? "Duplicate value.");
            else if (status === 403) setError("You don’t have permission to manage sources.");
            else setError(e?.response?.data?.detail ?? e?.message ?? "Failed to create source.");
        }
    }

    async function saveEdit() {
        if (editingId == null) return;

        const type = editingType.trim();
        if (!type) {
            setError("Type is required.");
            return;
        }

        const current = items.find(i => i.id === editingId);
        if (!current) return;

        // client-side duplicate check (ignore self)
        const dup = items.some(i => i.id !== editingId && norm(i.type) === norm(type));
        if (dup) {
            setError(`"${type}" already exists.`);
            return;
        }

        setError(null);
        try {
            const accessToken = await getAccessToken();
            if (!accessToken) throw new Error('Invalid access token');

            const dto: UpdateSourceDto = {
                type: type,
            }

            const updated = await putJson<SourceDto>(`admin/sources/${editingId}`, accessToken, JSON.stringify(dto));

            setItems(prev =>
                prev.map(i => (i.id === editingId ? updated : i)).sort((a, b) => a.type.localeCompare(b.type))
            );
            cancelEdit();
        } catch (e: any) {
            const status = e?.response?.status;
            if (status === 409) setError(e?.response?.data?.detail ?? "Duplicate value.");
            else if (status === 403) setError("You don’t have permission to manage sources.");
            else setError(e?.response?.data?.detail ?? e?.message ?? "Failed to update source.");
        }
    }

    async function confirmDelete() {
        if (!deleteTarget) return;

        setDeleting(true);
        setError(null);

        try {
            const accessToken = await getAccessToken();
            if (!accessToken) throw new Error('Invalid access token');

            await del(`admin/sources/${deleteTarget.id}`, accessToken);
            setItems(prev => prev.filter(i => i.id !== deleteTarget.id));
            if (editingId === deleteTarget.id) cancelEdit();
            setDeleteTarget(null);
        } catch (e: any) {
            const status = e?.response?.status;
            if (status === 409)
                setError(e?.response?.data?.detail ?? "Cannot delete: source is in use.");
            else if (status === 403)
                setError("You don’t have permission to manage sources.");
            else
                setError(e?.response?.data?.detail ?? e?.message ?? "Failed to delete source.");
        } finally {
            setDeleting(false);
        }
    }

    return (
        <div style={{ padding: 24, maxWidth: 900 }}>
            <h2>Admin · Artwork Sources</h2>
            <p style={{ marginTop: 8 }}>
                Manage global “Source” reference data (unique values only).
            </p>

            {error && (
                <div style={{ marginTop: 12, padding: 12, border: "1px solid #ddd" }}>
                    {error}
                </div>
            )}

            <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
                <input
                    value={newType}
                    onChange={(e) => setNewType(e.target.value)}
                    placeholder="Add a new source type (e.g., Dealer)"
                    style={{ flex: 1, padding: 8 }}
                    onKeyDown={(e) => { if (e.key === "Enter") void addSource(); }}
                />
                <button onClick={() => void addSource()} disabled={!newType.trim()}>
                    Add
                </button>
                <button onClick={() => void load()}>
                    Refresh
                </button>
            </div>

            <div style={{ marginTop: 16 }}>
                {loading ? (
                    <div>Loading…</div>
                ) : (
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                            <tr>
                                <th style={{ textAlign: "left", borderBottom: "1px solid #ddd", padding: 8 }}>Type</th>
                                <th style={{ width: 220, borderBottom: "1px solid #ddd", padding: 8 }}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((i) => {
                                const isEditing = editingId === i.id;

                                return (
                                    <tr key={i.id}>
                                        <td style={{ borderBottom: "1px solid #eee", padding: 8 }}>
                                            {isEditing ? (
                                                <input
                                                    value={editingType}
                                                    onChange={(e) => setEditingType(e.target.value)}
                                                    style={{ width: "100%", padding: 8 }}
                                                    onKeyDown={(e) => { if (e.key === "Enter") void saveEdit(); }}
                                                />
                                            ) : (
                                                i.type
                                            )}
                                        </td>

                                        <td style={{ borderBottom: "1px solid #eee", padding: 8, textAlign: "right" }}>
                                            {isEditing ? (
                                                <>
                                                    <button onClick={() => void saveEdit()} style={{ marginRight: 8 }}>
                                                        Save
                                                    </button>
                                                    <button onClick={cancelEdit}>
                                                        Cancel
                                                    </button>
                                                </>
                                            ) : (
                                                <>
                                                    <button onClick={() => startEdit(i)} style={{ marginRight: 8 }}>
                                                        Edit
                                                    </button>
                                                        <button onClick={() => setDeleteTarget(i)}>
                                                        Delete
                                                    </button>
                                                </>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                            {items.length === 0 && (
                                <tr>
                                    <td colSpan={2} style={{ padding: 12 }}>
                                        No sources found.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                )}
            </div>
            {
                deleteTarget && (
                    <div style={overlayStyle}>
                        <div style={modalStyle}>
                            <h3>Delete Source</h3>
                            <p>
                                Are you sure you want to delete "<strong>{deleteTarget.type}</strong>"?
                            </p>

                            <div style={{ marginTop: 16, textAlign: "right" }}>
                                <button
                                    onClick={() => setDeleteTarget(null)}
                                    disabled={deleting}
                                    style={{ marginRight: 8 }}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => void confirmDelete()}
                                    disabled={deleting}
                                >
                                    {deleting ? "Deleting..." : "Delete"}
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }
        </div>



    );
}
